import React, { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { Box, Grid2, Theme, useMediaQuery } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  CACHE_KEYS,
  CreateOrUpdateProfileParams,
  Profile,
  useUpdateUserProfile,
} from "@rentablo/api/apiv1";
import { useMandatorVisibility } from "@helpers";
import { renderFormElement } from "@helpers/renderFormElement";
import { FormElement } from "@helpers/types";
import { SendKpmgReportSection } from "./SendKpmgReportSection";

type Props = {
  userProfile: Profile;
};

export const NotificationsForm = ({ userProfile }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isKpmg = useMandatorVisibility({
    showFor: ["kpmg-germany"],
  });

  const { mutate: updateUserProfile } = useUpdateUserProfile({
    onSuccess: () => {
      enqueueSnackbar(t("settings.changesSaved"), {
        autoHideDuration: 2000,
        variant: "success",
        preventDuplicate: true,
      });
    },
  });

  const { control, watch } = useForm<CreateOrUpdateProfileParams>({
    defaultValues: {
      emailNotification: userProfile.emailNotification,
      investmentReportFrequency: userProfile.investmentReportFrequency,
    },
  });

  const saveProfile = useDebouncedCallback(
    (values: CreateOrUpdateProfileParams) => {
      updateUserProfile(
        {
          id: userProfile.id!,
          createOrUpdateProfileParams: values,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: [CACHE_KEYS.USER_PROFILE],
            });
          },
        }
      );
    },
    500
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch subscription
    const subscription = watch((values, { type }) => {
      if (type === "change") saveProfile(values);
    });
    return () => subscription.unsubscribe();
  }, [watch, saveProfile]);

  const formFields: FormElement[] = [
    {
      name: "investmentReportFrequency",
      type: "radio-group",
      options: [
        {
          value: 1,
          testId: "1",
          label: t(
            "settings.notifications.form.investmentReportFrequency_weekly"
          ),
        },
        ...(isKpmg
          ? [
              {
                value: 3,
                testId: "3",
                label: t(
                  "settings.notifications.form.investmentReportFrequency_12days"
                ),
              },
            ]
          : []),
        {
          value: 2,
          testId: "2",
          label: t(
            "settings.notifications.form.investmentReportFrequency_monthly"
          ),
        },
        {
          value: 9,
          testId: "9",
          label: t(
            "settings.notifications.form.investmentReportFrequency_never"
          ),
        },
      ],
    },
    {
      name: "emailNotification",
      type: "radio-group",
      size: useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"))
        ? "small"
        : "default",
      options: [
        {
          label: t("settings.notifications.form.emailNotification_yes"),
          value: true,
          testId: "yes",
        },
        {
          label: t("settings.notifications.form.emailNotification_no"),
          value: false,
          testId: "no",
        },
      ],
    },
  ];

  return (
    <Box>
      <Grid2 container rowSpacing={6} mb={11}>
        <Grid2
          size={{
            xs: 12,
            md: 12,
          }}
        >
          {renderFormElement(
            "settings.notifications.form",
            formFields[0],
            control
          )}
        </Grid2>
        {!isKpmg && (
          <Grid2
            size={{
              xs: 12,
              md: 12,
            }}
          >
            {renderFormElement(
              "settings.notifications.form",
              formFields[1],
              control
            )}
          </Grid2>
        )}
      </Grid2>

      {userProfile.userId && (
        <SendKpmgReportSection userId={userProfile.userId} />
      )}
    </Box>
  );
};
